import { signal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { setSoundOn, soundOn } from "../host/sound-pref.js";
import { hourNote } from "./hour.js";
import type { Volume } from "./settings.js";
import { reduceMotion, setSetting, settings } from "./settings.js";

/** Whether the settings sheet is open; any Settings button opens the one sheet (App.tsx). */
export const settingsOpen = signal(false);

export function openSettings(): void {
  settingsOpen.value = true;
}

const VOLUME_NAMES: Record<Volume, string> = { quiet: "Quiet", normal: "Normal", loud: "Loud" };

/**
 * How Obean feels (P5-UX-07): sound and how loud, buzzing on phones, less motion, larger text and
 * the note after an hour. Each takes effect at once and stays in this browser.
 */
export function SettingsSheet() {
  if (!settingsOpen.value) return null;
  return <Sheet />;
}

function Sheet() {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  const s = settings.value;
  const close = () => {
    settingsOpen.value = false;
  };
  const check = (
    id: string,
    label: string,
    checked: boolean,
    set: (on: boolean) => void,
    hint?: string,
  ) => (
    <label for={id} class="check">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          set(e.currentTarget.checked);
        }}
      />
      <span>
        {label}
        {hint && <span class="hint">{hint}</span>}
      </span>
    </label>
  );
  return (
    <dialog ref={dialog} class="report settings" aria-labelledby="settings-title" onClose={close}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          close();
        }}
      >
        <h2 id="settings-title">Settings</h2>
        {check("set-sound", "Sound", soundOn.value, setSoundOn)}
        <fieldset disabled={!soundOn.value}>
          <legend>How loud</legend>
          <div class="choices">
            {(Object.keys(VOLUME_NAMES) as Volume[]).map((v) => (
              <label key={v}>
                <input
                  type="radio"
                  name="volume"
                  checked={s.volume === v}
                  onChange={() => {
                    setSetting("volume", v);
                  }}
                />
                {VOLUME_NAMES[v]}
              </label>
            ))}
          </div>
        </fieldset>
        {check(
          "set-vibration",
          "Buzz on phones",
          s.vibration,
          (on) => {
            setSetting("vibration", on);
          },
          "A short buzz when you score, on phones that can.",
        )}
        <fieldset>
          <legend>Less motion</legend>
          <div class="choices">
            <label>
              <input
                type="radio"
                name="motion"
                checked={s.motion === "phone"}
                onChange={() => {
                  setSetting("motion", "phone");
                }}
              />
              Like my device
            </label>
            <label>
              <input
                type="radio"
                name="motion"
                checked={s.motion === "reduce"}
                onChange={() => {
                  setSetting("motion", "reduce");
                }}
              />
              Always
            </label>
          </div>
          <p class="hint" role="status">
            {reduceMotion.value ? "Less motion is on." : "Less motion is off."}
          </p>
        </fieldset>
        {check("set-text", "Larger text", s.largerText, (on) => {
          setSetting("largerText", on);
        })}
        {check(
          "set-hour",
          "A note after an hour of play",
          s.hourNote,
          (on) => {
            setSetting("hourNote", on);
          },
          "Once, between matches. Nothing is sent anywhere.",
        )}
        <p class="hint">These stay in this browser.</p>
        <div class="actions">
          <button type="submit">Done</button>
        </div>
      </form>
    </dialog>
  );
}

/** The note after an hour (ADR 0043), on a result: kind, once, and gone with a tap. */
export function HourNote() {
  if (!hourNote.value) return null;
  return (
    <p class="tip hour-note" role="status">
      You've played for an hour. A good moment to stretch, drink some water, or call it a day.{" "}
      <button
        type="button"
        class="link"
        onClick={() => {
          hourNote.value = false;
        }}
      >
        OK
      </button>
    </p>
  );
}
